import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { reviewAPI, emailAPI } from '../services/api';
import type { Email, EmailCategory } from '../types';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select-new';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { capitalize } from '@/lib/utils';
import { CheckCircle, XCircle, ArrowUpDown } from 'lucide-react';

const CATEGORIES: EmailCategory[] = [
  'customer_question',
  'product_question',
  'shipping_question',
  'damaged_defective',
  'wrong_item',
  'order_cancellation',
  'return_policy',
  'pending_response',
];

interface ReviewQueueProps {
  showAll?: boolean;
}

export default function ReviewQueue({ showAll = false }: ReviewQueueProps) {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [selectedTimeFilter, setSelectedTimeFilter] = useState<string>('');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [orderIdSearch, setOrderIdSearch] = useState<string>('');
  const [debouncedOrderId, setDebouncedOrderId] = useState<string>('');
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());
  const [selectedLimit, setSelectedLimit] = useState<string>('all');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  // Get status from URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('status');
    if (status) {
      setSelectedStatus(status);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedOrderId(orderIdSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [orderIdSearch]);

  const { data: emails, isLoading, refetch } = useQuery({
    queryKey: ['emails-v2', showAll, selectedCategory, selectedStatus, selectedTimeFilter, customStartDate, customEndDate, debouncedOrderId],
    queryFn: async () => {
      const params: any = {
        category: selectedCategory && selectedCategory !== 'all' ? selectedCategory : undefined,
      };

      if (selectedTimeFilter === 'custom' && customStartDate) {
        params.start_time = new Date(customStartDate).toISOString();
      }
      if (selectedTimeFilter === 'custom' && customEndDate) {
        params.end_time = new Date(customEndDate).toISOString();
      }

      if (selectedTimeFilter && selectedTimeFilter !== 'custom') {
        const now = new Date();
        let startTime = new Date();
        
        switch (selectedTimeFilter) {
          case '5min':
            startTime = new Date(now.getTime() - 5 * 60 * 1000);
            break;
          case '15min':
            startTime = new Date(now.getTime() - 15 * 60 * 1000);
            break;
          case '1hour':
            startTime = new Date(now.getTime() - 60 * 60 * 1000);
            break;
          case 'today':
            startTime = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            break;
          case 'week':
            startTime = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            break;
        }
        params.start_time = startTime.toISOString();
      }

      if (showAll) {
        const response = await emailAPI.list(params);
        let data = response.data;
        
        // Filter by status
        if (selectedStatus && selectedStatus !== 'all') {
          data = data.filter((email: Email) => email.response_status === selectedStatus);
        }
        
        if (debouncedOrderId) {
          data = data.filter((email: Email) => 
            email.extracted_entities?.order_id?.toLowerCase().includes(debouncedOrderId.toLowerCase())
          );
        }
        
        return data;
      } else {
        const response = await reviewAPI.getPending(params);
        let data = response.data;
        
        if (debouncedOrderId) {
          data = data.filter((email: Email) => 
            email.extracted_entities?.order_id?.toLowerCase().includes(debouncedOrderId.toLowerCase())
          );
        }
        
        return data;
      }
    },
  });

  const handleBulkApprove = async () => {
    if (selectedEmails.size === 0) return;
    
    try {
      await reviewAPI.bulkApprove(Array.from(selectedEmails), 'admin');
      toast.success(`Approved ${selectedEmails.size} emails`);
      setSelectedEmails(new Set());
      refetch();
    } catch (error) {
      toast.error('Failed to approve emails');
    }
  };

  const handleBulkReject = async () => {
    if (selectedEmails.size === 0) return;
    
    try {
      await reviewAPI.bulkReject(Array.from(selectedEmails), 'admin');
      toast.success(`Rejected ${selectedEmails.size} emails`);
      setSelectedEmails(new Set());
      refetch();
    } catch (error) {
      toast.error('Failed to reject emails');
    }
  };

  const handleBulkReprocess = async () => {
    if (selectedEmails.size === 0) return;
    
    try {
      await reviewAPI.bulkReprocess(Array.from(selectedEmails));
      toast.success(`Reprocessed ${selectedEmails.size} emails`);
      setSelectedEmails(new Set());
      refetch();
    } catch (error) {
      toast.error('Failed to reprocess emails');
    }
  };

  const handleQuickApprove = async (id: string) => {
    try {
      await reviewAPI.approve(id, 'admin');
      toast.success('Email approved');
      refetch();
    } catch (error) {
      toast.error('Failed to approve email');
    }
  };

  const handleQuickApproveAndSend = async (id: string) => {
    try {
      await reviewAPI.approveAndSend(id, 'admin');
      toast.success('Email approved and sent');
      refetch();
    } catch (error) {
      toast.error('Failed to approve and send email');
    }
  };

  const handleQuickReject = async (id: string) => {
    try {
      await reviewAPI.reject(id, 'admin');
      toast.success('Email rejected');
      refetch();
    } catch (error) {
      toast.error('Failed to reject email');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Apply limit filter
  const limitedEmails = selectedLimit === 'all' 
    ? emails 
    : emails?.slice(0, parseInt(selectedLimit));

  // Apply sort order
  const sortedEmails = sortOrder === 'oldest' 
    ? [...(limitedEmails || [])].reverse() 
    : limitedEmails;

  const totalPages = Math.ceil((sortedEmails?.length || 0) / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedEmails = sortedEmails?.slice(startIndex, endIndex);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {showAll ? 'All Emails' : 'Review Queue'}
          </h1>
          <p className="text-muted-foreground mt-2">
            {showAll ? 'Complete email history' : 'Review and approve pending emails'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {selectedEmails.size > 0 && (
            <>
              {selectedStatus === 'pending_review' && (
                <>
                  <Button 
                    onClick={handleBulkApprove}
                    variant="outline"
                    className="border-green-200 text-green-700 hover:bg-green-50 min-w-[180px]"
                  >
                    Approve {selectedEmails.size} Selected
                  </Button>
                  <Button 
                    onClick={handleBulkReject}
                    variant="outline"
                    className="border-red-200 text-red-700 hover:bg-red-50 min-w-[180px]"
                  >
                    Reject {selectedEmails.size} Selected
                  </Button>
                </>
              )}
              {selectedStatus === 'rejected' && (
                <Button 
                  onClick={handleBulkReprocess}
                  variant="outline"
                  className="border-blue-200 text-blue-700 hover:bg-blue-50 min-w-[180px]"
                >
                  Reprocess {selectedEmails.size} Selected
                </Button>
              )}
            </>
          )}
          {paginatedEmails && paginatedEmails.length > 0 && (
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={paginatedEmails.every((email: Email) => selectedEmails.has(email.id))}
                onCheckedChange={(checked: boolean) => {
                  if (checked) {
                    const newSelected = new Set(selectedEmails);
                    paginatedEmails.forEach((email: Email) => newSelected.add(email.id));
                    setSelectedEmails(newSelected);
                  } else {
                    const newSelected = new Set(selectedEmails);
                    paginatedEmails.forEach((email: Email) => newSelected.delete(email.id));
                    setSelectedEmails(newSelected);
                  }
                }}
                className="h-4 w-4 rounded border-input"
              />
              <span className="text-muted-foreground">Select All</span>
            </label>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center gap-2">
            {showAll && (
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger className="h-9 w-[180px]">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending_review">Pending Review</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            )}
            
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="h-9 w-[180px]">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {CATEGORIES.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {capitalize(cat)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={selectedTimeFilter} onValueChange={(value) => {
              setSelectedTimeFilter(value);
              if (value !== 'custom') {
                setCustomStartDate('');
                setCustomEndDate('');
              }
            }}>
              <SelectTrigger className="h-9 w-[180px]">
                <SelectValue placeholder="All Time" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="5min">Past 5 minutes</SelectItem>
                <SelectItem value="15min">Past 15 minutes</SelectItem>
                <SelectItem value="1hour">Past hour</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="week">Past week</SelectItem>
                <SelectItem value="custom">Custom Range</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={selectedLimit} onValueChange={setSelectedLimit}>
              <SelectTrigger className="h-9 w-[180px]">
                <SelectValue placeholder="Show All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Show All</SelectItem>
                <SelectItem value="5">Last 5 emails</SelectItem>
                <SelectItem value="10">Last 10 emails</SelectItem>
                <SelectItem value="25">Last 25 emails</SelectItem>
                <SelectItem value="50">Last 50 emails</SelectItem>
                <SelectItem value="100">Last 100 emails</SelectItem>
              </SelectContent>
            </Select>
            
            <Button
              onClick={() => setSortOrder(sortOrder === 'newest' ? 'oldest' : 'newest')}
              variant="outline"
              size="sm"
              className="h-9"
            >
              <ArrowUpDown className="h-4 w-4 mr-2" />
              {sortOrder === 'newest' ? 'Newest First' : 'Oldest First'}
            </Button>
            
            {selectedTimeFilter === 'custom' && (
              <>
                <Input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="h-9 w-auto"
                />
                <span className="text-muted-foreground text-sm">to</span>
                <Input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="h-9 w-auto"
                />
              </>
            )}

            <Input
              type="text"
              value={orderIdSearch}
              onChange={(e) => setOrderIdSearch(e.target.value)}
              placeholder="Search by Order ID"
              className="h-9 w-auto min-w-[200px]"
            />
          </div>
        </CardContent>
      </Card>

      {/* Email List */}
      <div className="space-y-4">
        {paginatedEmails?.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">No emails found</p>
            </CardContent>
          </Card>
        ) : (
          paginatedEmails?.map((email: Email) => (
            <Card
              key={email.id}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate(`/review/${email.id}`)}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={selectedEmails.has(email.id)}
                        onCheckedChange={(checked: boolean) => {
                          const newSelected = new Set(selectedEmails);
                          if (checked) {
                            newSelected.add(email.id);
                          } else {
                            newSelected.delete(email.id);
                          }
                          setSelectedEmails(newSelected);
                        }}
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      />
                      <div>
                        <h3 className="font-semibold">{email.subject}</h3>
                        <p className="text-sm text-muted-foreground">
                          From: {email.from_address.split('<')[0].trim() || email.from_address}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-7">
                      <Badge variant="secondary">
                        {capitalize(email.category || 'N/A')}
                      </Badge>
                      {email.extracted_entities?.order_id && (
                        <Badge variant="outline">
                          Order: {email.extracted_entities.order_id}
                        </Badge>
                      )}
                      <span className="text-xs text-muted-foreground">
                        {new Date(email.received_at || email.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                  {!showAll && (
                    <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-green-200 text-green-700 hover:bg-green-50"
                        onClick={() => handleQuickApprove(email.id)}
                      >
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-green-200 text-green-700 hover:bg-green-50"
                        onClick={() => handleQuickApproveAndSend(email.id)}
                      >
                        Approve & Send
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-200 text-red-700 hover:bg-red-50"
                        onClick={() => handleQuickReject(email.id)}
                      >
                        Reject
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {startIndex + 1} to {Math.min(endIndex, sortedEmails?.length || 0)} of {sortedEmails?.length || 0} results
            {selectedLimit !== 'all' && ` (filtered from ${emails?.length || 0} total)`}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            
            {/* First page */}
            {currentPage > 3 && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(1)}
                >
                  1
                </Button>
                {currentPage > 4 && <span className="px-2 py-1 text-sm">...</span>}
              </>
            )}
            
            {/* Pages around current */}
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(page => page >= currentPage - 2 && page <= currentPage + 2)
              .map(page => (
                <Button
                  key={page}
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(page)}
                  className={currentPage === page ? 'bg-muted' : ''}
                >
                  {page}
                </Button>
              ))}
            
            {/* Last page */}
            {currentPage < totalPages - 2 && (
              <>
                {currentPage < totalPages - 3 && <span className="px-2 py-1 text-sm">...</span>}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(totalPages)}
                >
                  {totalPages}
                </Button>
              </>
            )}
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
